import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import Svg, { Polyline } from 'react-native-svg';
import { Colors } from '@constants/colors';

interface MiniSparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  strokeWidth?: number;
}

export const MiniSparkline: React.FC<MiniSparklineProps> = ({
  data,
  width = 60,
  height = 30,
  color,
  strokeWidth = 1.5,
}) => {
  const points = useMemo(() => {
    if (!data || data.length === 0) return '';

    // Find min and max for scaling
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1; // Avoid division by zero

    // Calculate points
    const xStep = width / (data.length - 1);
    const padding = 2;
    const usableHeight = height - padding * 2;

    const pointsStr = data
      .map((value, index) => {
        const x = index * xStep;
        // Invert Y because SVG Y goes down
        const y = padding + usableHeight - ((value - min) / range) * usableHeight;
        return `${x},${y}`;
      })
      .join(' ');

    return pointsStr;
  }, [data, width, height]);

  // Determine color based on trend (first vs last)
  const trendColor = useMemo(() => {
    if (color) return color;
    if (!data || data.length < 2) return Colors.text.tertiary;

    const first = data[0];
    const last = data[data.length - 1];
    return last >= first ? Colors.market.profit : Colors.market.loss;
  }, [data, color]);

  if (!data || data.length === 0) {
    return <View style={[styles.container, { width, height }]} />;
  }

  return (
    <View style={[styles.container, { width, height }]}>
      <Svg width={width} height={height}>
        <Polyline
          points={points}
          fill="none"
          stroke={trendColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Svg>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});
